import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Trash2, Plus, Shield } from 'lucide-react'

export default function ThreatDetectionPage() {
  const navigate = useNavigate()
  const [threats, setThreats] = useState([
    { id: 1, type: 'Malware', source: '203.45.23.156', destination: '192.168.1.50', severity: 'Critical', status: 'Blocked', detected: '2 min ago' },
    { id: 2, type: 'Port Scan', source: '45.89.123.45', destination: 'Network', severity: 'High', status: 'Blocked', detected: '15 min ago' },
    { id: 3, type: 'SQL Injection', source: '67.34.12.89', destination: '192.168.1.100', severity: 'Critical', status: 'Blocked', detected: '1 hour ago' },
    { id: 4, type: 'Brute Force', source: '123.45.67.89', destination: '192.168.1.25', severity: 'Medium', status: 'Blocked', detected: '3 hours ago' },
    { id: 5, type: 'DDoS Attack', source: 'Multiple', destination: '10.0.0.1', severity: 'Critical', status: 'Mitigated', detected: '5 hours ago' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({ type: '', source: '', destination: '', severity: 'High' })

  const stats = useMemo(() => ({
    totalThreats: threats.length,
    criticalThreats: threats.filter(t => t.severity === 'Critical').length,
    blockedThreats: threats.filter(t => t.status === 'Blocked').length,
    mitigatedThreats: threats.filter(t => t.status === 'Mitigated').length
  }), [threats])

  const handleAddThreat = () => {
    setFormData({ type: '', source: '', destination: '', severity: 'High' })
    setShowModal(true)
  }

  const handleSave = () => {
    if (!formData.type || !formData.source) return alert('Type and source required')
    setThreats([...threats, { id: Date.now(), ...formData, status: 'Detected', detected: 'Just now' }])
    setShowModal(false)
  }

  const handleDelete = (id) => {
    if (confirm('Remove this threat from logs?')) {
      setThreats(threats.filter(t => t.id !== id))
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-red-600/20 text-red-400 border-red-500/30'
      case 'High': return 'bg-orange-600/20 text-orange-400 border-orange-500/30'
      case 'Medium': return 'bg-yellow-600/20 text-yellow-400 border-yellow-500/30'
      case 'Low': return 'bg-green-600/20 text-green-400 border-green-500/30'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Threat Detection</h1>
              <p className="text-slate-400">Detect and prevent network-based threats</p>
            </div>
            <button onClick={handleAddThreat} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> Log Threat
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Threats</div>
            <div className="text-3xl font-bold text-red-400">{stats.totalThreats}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Critical</div>
            <div className="text-3xl font-bold text-red-500">{stats.criticalThreats}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Blocked</div>
            <div className="text-3xl font-bold text-green-400">{stats.blockedThreats}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Mitigated</div>
            <div className="text-3xl font-bold text-blue-400">{stats.mitigatedThreats}</div>
          </div>
        </div>

        <div className="space-y-4">
          {threats.map((threat) => (
            <div key={threat.id} className={`rounded-xl border p-6 ${getSeverityColor(threat.severity)}`}>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <AlertTriangle size={20} />
                    <h3 className="text-lg font-semibold">{threat.type}</h3>
                    <span className="px-2 py-1 rounded text-xs font-bold bg-slate-700/50">
                      {threat.severity}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${threat.status === 'Blocked' ? 'bg-green-600/30' : 'bg-blue-600/30'}`}>
                      {threat.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                      <p className="text-xs opacity-75">Source</p>
                      <p className="font-mono text-sm mt-1">{threat.source}</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-75">Destination</p>
                      <p className="font-mono text-sm mt-1">{threat.destination}</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-75">Detected</p>
                      <p className="font-mono text-sm mt-1">{threat.detected}</p>
                    </div>
                  </div>
                </div>
                <button onClick={() => handleDelete(threat.id)} className="p-2 hover:bg-white/10 rounded">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">Log Threat Detection</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} placeholder="Threat Type" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})} placeholder="Source IP" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.destination} onChange={(e) => setFormData({...formData, destination: e.target.value})} placeholder="Destination IP/Network" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <select value={formData.severity} onChange={(e) => setFormData({...formData, severity: e.target.value})} className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                  <option>Critical</option>
                </select>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSave} className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold">Log Threat</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
